import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { setUserContext, trackEvent } from "./analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("cf_token");
        if (!token) { setLoading(false); return; }
        api.get("/auth/me").then(r => {
            setUser(r.data);
            setUserContext({
                user_id: r.data?.id, role: r.data?.role,
                org_id: r.data?.org_id, org_name: r.data?.org_name,
            });
        }).catch(() => {
            localStorage.removeItem("cf_token");
        }).finally(() => setLoading(false));
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("cf_token", data.token);
        setUser(data.user);
        setUserContext({
            user_id: data.user?.id, role: data.user?.role,
            org_id: data.user?.org_id, org_name: data.user?.org_name,
        });
        trackEvent("login", { method: "password", role: data.user?.role });
        return data.user;
    };

    const register = async (payload) => {
        // Stamp competitor-referral source if captured during the session
        let signup_source = null;
        try {
            const mod = await import("./signupSource");
            signup_source = mod.readSignupSource();
        } catch { /* noop */ }
        const body = signup_source ? { ...payload, signup_source } : payload;
        const { data } = await api.post("/auth/register", body);
        localStorage.setItem("cf_token", data.token);
        setUser(data.user);
        setUserContext({
            user_id: data.user?.id, role: data.user?.role,
            org_id: data.user?.org_id, org_name: data.user?.org_name,
        });
        trackEvent("sign_up", {
            method: "password",
            role: data.user?.role,
            signup_source: signup_source || "direct",
        });
        return data.user;
    };

    const logout = () => {
        localStorage.removeItem("cf_token");
        setUser(null);
        trackEvent("logout");
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
